import React from "react";
import {
  Container,
  Header,
  SpaceBetween,
  Grid,
  Button,
  Alert,
  Box,
} from "@amzn/awsui-components-react";
import { WizardConfig } from "../generic-draft-wizard";
import CreateEditGaiaobjectForm from "../create-edit-gaiaobject-form/CreateEditGaiaobjectForm";
import { FilePreview } from "../file-preview/FilePreview";
import AssessmentDetailsStep from "../assessment-details-step/AssessmentDetailsStep";
import PaymentDetailsStep from "../payment-details-step/PaymentDetailsStep";
import { GAIAOBJECT_CATEGORY } from "utils/gaia-constants";
import { ScanStepContentConfig } from "./definitions";

/**
 * Shared helper for building the scan wizard step content (Assessment or Payment).
 */
function createScanStepContent({
  shortId,
  isSimplified,
  setIsSimplified,
  addLabel,
  skipLabel,
  simplifiedMessage,
  noDataMessage,
  detailsComponent,
  filePreviewToggleButton,
  gridDefinition,
  isFilePreviewVisible,
  filePreviewContainer,
}: ScanStepContentConfig): React.ReactNode {
  if (!shortId) {
    return <Alert type="info">{noDataMessage}</Alert>;
  }

  return (
    <SpaceBetween size="m">
      {filePreviewToggleButton}
      <Grid gridDefinition={gridDefinition}>
        {isFilePreviewVisible && filePreviewContainer}
        <Container
          header={
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              {isSimplified ? (
                <Button onClick={() => setIsSimplified?.(false)}>
                  {addLabel}
                </Button>
              ) : (
                <Button onClick={() => setIsSimplified?.(true)}>
                  {skipLabel}
                </Button>
              )}
            </div>
          }
        >
          {isSimplified ? (
            <Box textAlign="left" color="inherit">
              <Box variant="p">{simplifiedMessage}</Box>
            </Box>
          ) : (
            detailsComponent
          )}
        </Container>
      </Grid>
    </SpaceBetween>
  );
}

export const createNoticeWizardConfig = (
  onComplete: (stepData: any) => Promise<void>,
  onCancel: () => void,
  category: GAIAOBJECT_CATEGORY,
  scanJobData?: any,
  downloadUrlData?: any,
  shortId?: string,
  onStepValidationSuccess?: (data: any) => void,
  triggerWizardValidationCounter?: number,
  triggerFormValidation?: () => void,
  formRef?: React.RefObject<any>,
  isFilePreviewVisible?: boolean,
  setIsFilePreviewVisible?: (visible: boolean) => void,
  isAssessmentSimplified?: boolean,
  setIsAssessmentSimplified?: (value: boolean) => void,
  control?: any,
  setValue?: any,
  refetchDownloadUrl?: () => void,
  isPaymentSimplified?: boolean,
  setIsPaymentSimplified?: (value: boolean) => void,
  paymentControl?: any,
  paymentSetValue?: any,
  isFileScanPaymentEnabled?: boolean,
  isFileScanAssessmentEnabled?: boolean
): WizardConfig => {
  const filePreviewToggleButton = (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        variant="link"
        iconName={isFilePreviewVisible ? "view-horizontal" : "view-full"}
        onClick={() => {
          refetchDownloadUrl?.();
          setIsFilePreviewVisible?.(!isFilePreviewVisible);
        }}
      >
        {isFilePreviewVisible ? "Hide" : "Show"} File Preview
      </Button>
    </SpaceBetween>
  );

  const filePreviewContainer = scanJobData?.scanJob ? (
    <Container header={<Header variant="h2">File Preview</Header>}>
      <FilePreview
        type={"pdf"}
        fileName={scanJobData.scanJob.fileName}
        presignedUrl={downloadUrlData?.url || ""}
      />
    </Container>
  ) : (
    <Container header={<Header variant="h2">File Preview</Header>}>
      <div>No file available for preview</div>
    </Container>
  );

  const gridDefinition = isFilePreviewVisible
    ? [{ colspan: 6 }, { colspan: 6 }]
    : [{ colspan: 12 }];

  const sharedStepProps = {
    filePreviewToggleButton,
    gridDefinition,
    isFilePreviewVisible,
    filePreviewContainer,
  };

  return {
    category: category,
    submitButtonText: "Create",
    cancelButtonText: "Cancel",
    onComplete,
    onCancel,
    refetchDownloadUrl,
    steps: [
      // Step 1: Notice Details
      {
        title: "Notice Details",
        description: (
          <>
            <br />
            Fill in the notice information below. The form will be pre-populated
            with default values.
          </>
        ),
        validateWizardStep: async () => true,
        triggerFormValidation,
        content: (
          <SpaceBetween size="m">
            {filePreviewToggleButton}
            <Grid gridDefinition={gridDefinition}>
              {isFilePreviewVisible && filePreviewContainer}
              <CreateEditGaiaobjectForm
                ref={formRef}
                type="notice"
                mode="wizard"
                shortId={shortId}
                onWizardValidationSuccess={onStepValidationSuccess}
                triggerWizardValidation={triggerWizardValidationCounter || 0}
              />
            </Grid>
          </SpaceBetween>
        ),
      },

      // Step 2: Create Assessment
      ...(isFileScanAssessmentEnabled
        ? [
            {
              title: "Create Assessment",
              description:
                "Create a preliminary assessment based on the notice scan data",
              content: createScanStepContent({
                shortId,
                category,
                isSimplified: isAssessmentSimplified,
                setIsSimplified: setIsAssessmentSimplified,
                addLabel: "Add Assessment",
                skipLabel: "Skip Assessment",
                simplifiedMessage: `Create the ${category} now and add assessment later.`,
                noDataMessage:
                  "No assessment data available from the scan. You can skip this step and create an assessment later.",
                detailsComponent: (
                  <AssessmentDetailsStep
                    shortIdForScanJob={shortId}
                    isScanWizard={true}
                    currencySign="$"
                    category={category}
                    control={control}
                    setValue={setValue}
                  />
                ),
                ...sharedStepProps,
              }),
            },
          ]
        : []),

      // Step 3: Create Payment
      ...(isFileScanPaymentEnabled
        ? [
            {
              title: "Create Payment",
              description: "Create a payment based on the notice scan data",
              content: createScanStepContent({
                shortId,
                category,
                isSimplified: isPaymentSimplified,
                setIsSimplified: setIsPaymentSimplified,
                addLabel: "Add Payment",
                skipLabel: "Skip Payment",
                simplifiedMessage: `Create the ${category} now and add payment later.`,
                noDataMessage:
                  "No payment data available from the scan. You can skip this step and create a payment later.",
                detailsComponent: (
                  <PaymentDetailsStep
                    shortIdForScanJob={shortId}
                    isScanWizard={true}
                    currencySign="€"
                    category={category}
                    control={paymentControl}
                    setValue={paymentSetValue}
                  />
                ),
                ...sharedStepProps,
              }),
            },
          ]
        : []),
    ],
  };
};
